import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { areaSchema } from "../../../../models/area";
import { areaDefaults } from "../../../../models/area";
import PropTypes from "prop-types";
import { FormPhotoUpload, OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * AreaEditor
 * Formulário principal de Áreas (Campos: Nome, Foto, Largura, Comprimento, Status).
 * Migrado para o Neo Design System.
 */
const AreaEditor = ({ area, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: areaDefaults,
        validationSchema: areaSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando a área selecionada muda
    useEffect(() => {
        if (area) {
            formik.resetForm({
                values: {
                    ...areaDefaults,
                    ...area
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!area) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-map-marker-path"></i>
                        </div>
                    </div>
                    <h5>Selecione uma área</h5>
                    <p className="text-muted">Escolha uma área na lista ao lado ou crie uma nova para gerenciar as dimensões do seu espaço.</p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.name : "Nova Área"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Informações Básicas e Dimensões"
            headerIcon="mdi-map-marker"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="outline-danger"
                            square={true}
                            onClick={() => onDelete(formik.values.id)}
                            disabled={isSaving}
                            title="Excluir Área"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />
            
            <Row className="mb-4">
                <Col lg={12}>
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                        Dados Principais
                    </h6>
                </Col>

                <Col md="3" className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center justify-content-center">
                    <FormPhotoUpload
                        photoPreview={formik.values.photoUrl}
                        onChange={(url) => {
                            formik.setFieldValue("photoUrl", url);
                            onChange(true);
                        }}
                        size="lg"
                    />
                </Col>

                <Col md="9">
                    <Row>
                        <Col md="12" className="mb-3">
                            <NeoInput
                                label="Nome da Área"
                                name="name"
                                type="text"
                                flat={true}
                                placeholder="Ex: Tatame 1, Quadra de Tênis..."
                                error={formik.touched.name && formik.errors.name ? formik.errors.name : ""}
                                {...formik.getFieldProps("name")}
                            />
                        </Col>

                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Largura (m)"
                                name="width"
                                type="number"
                                step="0.1"
                                min="0"
                                flat={true}
                                placeholder="Ex: 10.5"
                                error={formik.touched.width && formik.errors.width ? formik.errors.width : ""}
                                {...formik.getFieldProps("width")}
                            />
                        </Col>

                        <Col md="4" className="mb-3">
                            <NeoInput
                                label="Comprimento (m)"
                                name="length"
                                type="number"
                                step="0.1"
                                min="0"
                                flat={true}
                                placeholder="Ex: 20.0"
                                error={formik.touched.length && formik.errors.length ? formik.errors.length : ""}
                                {...formik.getFieldProps("length")}
                            />
                        </Col>

                        <Col md="4" className="mb-3">
                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                <label className="neo-input-label">Status</label>
                                 <div className="form-check form-switch pt-2">
                                    <input
                                        type="checkbox"
                                        role="switch"
                                        checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                                        onChange={(e) => {
                                            formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                            onChange(true);
                                        }}
                                        className="form-check-input custom-switch-primary cursor-pointer"
                                        id="area-status-switch"
                                    />
                                    <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="area-status-switch">
                                        {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                                    </label>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </NeoCard>
    );
};

AreaEditor.propTypes = {
    area: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default AreaEditor;
